import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LoaderRenderer, useSciDomain } from 'components';
import { OrderStatus } from 'enums';
import { useOrderContextOptional } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { PaymentLoadingViewProps } from 'types';

import { SqPaymentFields } from '../SqPaymentFields';

export const SqPaymentLoading: React.FC<PaymentLoadingViewProps> = ({
  status,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_loading',
  });

  const { data: { loader } = {} } = useSciDomain();
  // Rendered both inside BaseOrderView (Provider present) and by OrderPage during viewModel.isLoading
  // (Provider not yet mounted) — read the context optionally to get shouldShowQrCode when available.
  const orderCtx = useOrderContextOptional();
  const shouldShowQrCode = orderCtx?.shouldShowQrCode;

  const loaderElement = (
    <LoaderRenderer
      loaderUrl={loader}
      imageClassName="tw-w-48 tw-mb-4 tw-animate-pulse"
      showCountdown={status === OrderStatus.Requisites}
    />
  );

  const isQrLoading = useMemo(
    () => status === OrderStatus.TraderConfirm && shouldShowQrCode,
    [status, shouldShowQrCode],
  );

  const waitingTextElement = (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-text-center">
      <span className="tw-text-lg tw-font-medium tw-text-primary">
        {t('waiting_title')}
      </span>
      <span className="tw-text-sm tw-text-secondary">
        {t('waiting_subtitle')}
      </span>
    </div>
  );

  return (
    <Fragment>
      <div
        className={`tw-flex tw-flex-col tw-items-center tw-flex-1 tw-w-full tw-py-8 tw-gap-4 ${
          isQrLoading ? 'tw-justify-start' : 'tw-justify-center'
        }`}
      >
        {isQrLoading ? (
          <Fragment>
            <div className="tw-w-full tw-mb-12">
              <SqPaymentFields />
            </div>
            {loaderElement}
            {waitingTextElement}
          </Fragment>
        ) : (
          <Fragment>
            {loaderElement}
            {status === OrderStatus.Requisites && (
              <div className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-text-center">
                <span className="tw-text-lg tw-font-medium tw-text-primary">
                  {t('searching_title')}
                </span>
                <span className="tw-text-sm tw-text-secondary">
                  {t('searching_subtitle')}
                </span>
              </div>
            )}
            {status === OrderStatus.TraderConfirm && (
              <Fragment>
                {waitingTextElement}
                <div className="tw-w-full">
                  <SqPaymentFields />
                </div>
              </Fragment>
            )}
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};
